import { serverTimestamp } from 'firebase/firestore'
import {
  typedCollection,
  typedDoc,
  getCollection,
  writeBatch,
  deleteField,
  db,
  orderBy,
} from '@/lib/firebase/firestore'
import {
  listAuthUsersFn,
  backfillUserProfilesFn,
  repairUserMemberLinkFn,
  type AuthUserListItem,
} from '@/lib/firebase/functions'
import type { UserDocument } from '@/types/firebase.types'
import type { CompanyMemberDocument } from '@/types'

// Catalog row presented to the admin UI. `id` is always the auth uid so
// existing link/role mutations keep working unchanged.
export type LinkUserRow = AuthUserListItem & {
  id: string
}

export interface LinkIntegrityIssue {
  kind: 'member_points_to_missing_user' | 'user_points_to_missing_member' | 'mismatched_pair'
  memberId?: string
  memberName?: string
  userId?: string
  userEmail?: string | null
  detail: string
}

export const adminUserLinksService = {
  // Authoritative list comes from Firebase Auth (via callable) so users from
  // any provider show up immediately, even before a /users doc exists.
  async getAllUsers(): Promise<LinkUserRow[]> {
    const { data } = await listAuthUsersFn({})
    return data.users
      .map((u) => ({ ...u, id: u.uid }))
      .sort((a, b) => {
        const nameA = (a.displayName ?? a.email ?? '').toLowerCase()
        const nameB = (b.displayName ?? b.email ?? '').toLowerCase()
        return nameA.localeCompare(nameB)
      })
  },

  async getAllMembers(): Promise<Array<CompanyMemberDocument & { id: string }>> {
    return getCollection(
      typedCollection<CompanyMemberDocument>('company'),
      orderBy('order', 'asc')
    )
  },

  async linkUserToMember(userId: string, memberId: string): Promise<void> {
    // Use set+merge so this works even if the /users doc doesn't exist yet
    // (e.g. a Google SSO user who hasn't been backfilled). The repair
    // callable does the same on the backend.
    await repairUserMemberLinkFn({ userId, memberId })
  },

  async unlinkUserFromMember(userId: string, memberId: string): Promise<void> {
    const batch = writeBatch(db)
    const userRef = typedDoc<UserDocument>('users', userId)
    const memberRef = typedDoc<CompanyMemberDocument>('company', memberId)
    batch.update(userRef, { linkedMemberId: deleteField(), updatedAt: serverTimestamp() })
    batch.update(memberRef, { linkedUserId: deleteField(), updatedAt: serverTimestamp() })
    await batch.commit()
  },

  async backfillProfiles(): Promise<{ scanned: number; created: number; alreadyPresent: number }> {
    const { data } = await backfillUserProfilesFn({})
    return data
  },

  async repairLink(userId: string, memberId: string): Promise<void> {
    await repairUserMemberLinkFn({ userId, memberId })
  },

  // Pure function — given the loaded users + members, return any broken
  // bidirectional pointers so the UI can offer a "repair" button.
  computeIntegrityIssues(
    users: LinkUserRow[],
    members: Array<CompanyMemberDocument & { id: string }>
  ): LinkIntegrityIssue[] {
    const usersById = new Map(users.map((u) => [u.id, u]))
    const membersById = new Map(members.map((m) => [m.id, m]))
    const issues: LinkIntegrityIssue[] = []

    for (const m of members) {
      if (!m.linkedUserId) continue
      const u = usersById.get(m.linkedUserId)
      if (!u) {
        issues.push({
          kind: 'member_points_to_missing_user',
          memberId: m.id,
          memberName: m.name,
          userId: m.linkedUserId,
          detail: `Member "${m.name}" links to user ${m.linkedUserId}, which does not exist in Auth.`,
        })
        continue
      }
      if (u.linkedMemberId !== m.id) {
        issues.push({
          kind: 'mismatched_pair',
          memberId: m.id,
          memberName: m.name,
          userId: u.id,
          userEmail: u.email,
          detail: `Member "${m.name}" links to user ${u.email ?? u.id}, but the user's linkedMemberId is "${u.linkedMemberId ?? '(none)'}".`,
        })
      }
    }

    for (const u of users) {
      if (!u.linkedMemberId) continue
      const m = membersById.get(u.linkedMemberId)
      if (!m) {
        issues.push({
          kind: 'user_points_to_missing_member',
          userId: u.id,
          userEmail: u.email,
          memberId: u.linkedMemberId,
          detail: `User ${u.email ?? u.id} links to member ${u.linkedMemberId}, which does not exist.`,
        })
      }
    }

    return issues
  },
}
